import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import auth0 from 'auth0-js';
import { environment } from 'environments/environment';
import { bindNodeCallback, Observable, of, ReplaySubject } from 'rxjs';
import { catchError, concatMap, defaultIfEmpty, filter, map } from 'rxjs/operators';
import { CookieService } from '../services/cookie.service';
import { AdminUser } from './models/admin-user';
import { CompanyUser } from './models/company-user';
import { createUser, CurrentUser } from './models/current-user';
import { Jwt } from './models/jwt';
import { PhysicianUser } from './models/physician-user';

@Injectable({
  providedIn: 'root',
})
export class AuthenticationService {
  currentUserChanged$: ReplaySubject<CurrentUser> = new ReplaySubject<CurrentUser>(1);

  badCredentials = false;

  currentUser: CurrentUser;

  private _accessToken = 'token';

  private _refreshTokenTimeout: any;

  private _auth0 = new auth0.WebAuth({
    domain: environment.auth.DOMAIN,
    clientID: environment.auth.CLIENT_ID,
    audience: environment.auth.AUDIENCE,
    prompt: 'none',
  });

  private _parseHash$ = bindNodeCallback(this._auth0.parseHash.bind(this._auth0));

  constructor(private _http: HttpClient, @Inject(PLATFORM_ID) private _platformId: Object, private _cookieService: CookieService) {}

  get jwt(): Jwt {
    return new Jwt(this._cookieService.get(this._accessToken));
  }

  login(username: string, password: string) {
    this._auth0.login(
      {
        realm: 'Username-Password-Authentication',
        username: username,
        password: password,
        responseType: 'token id_token',
        redirectUri: `${window.location.origin}`,
      },
      () => {
        this.badCredentials = true;
      }
    );
  }

  logout() {
    this.currentUserChanged$.next(null);
    this._auth0.logout({
      returnTo: environment.auth.LOGOUT_URL,
      clientID: environment.auth.CLIENT_ID,
    });
  }

  resetPassword(email: string): Observable<any> {
    return this._auth0.changePassword({ connection: 'Username-Password-Authentication', email: email }, () => {});
  }

  isAuthenticated(): boolean {
    return this._cookieService.exists(this._accessToken);
  }

  clearUserData() {
    if (isPlatformBrowser(this._platformId)) {
      this._cookieService.delete(this._accessToken);
    }
    this.currentUser = null;
    clearTimeout(this._refreshTokenTimeout);
  }

  signUp(email: string, password?: string): void {
    this._auth0.signup(
      {
        clientID: environment.auth.CLIENT_ID,
        email: email,
        password: password ? password : this.generatePassword(),
        connection: 'Username-Password-Authentication',
      },
      (success: any) => {
        if (!password) {
          // for new company user send email to reset password
          this.resetPassword(email);
        }
      }
    );
  }

  authenticateUser(): Observable<Jwt> {
    return this._parseHash$().pipe(
      map((authResult: any) => {
        const jwt = authResult ? new Jwt(authResult.accessToken) : null;
        if (jwt) {
          this.badCredentials = false;
          this._cookieService.set(this._accessToken, authResult.accessToken, jwt.exp);
        } else {
          this.clearUserData();
        }
        return jwt;
      }),
      catchError(() => {
        this.clearUserData();
        return of(null);
      })
    );
  }

  getUser(): Observable<CompanyUser | AdminUser | PhysicianUser> {
    if (isPlatformBrowser(this._platformId)) {
      const getJwt$ = this._cookieService.exists(this._accessToken) ? of(this.jwt) : this.authenticateUser();
      const getUser$ = (jwt: Jwt) => (this.currentUser == null ? this.getUserProfile(jwt.email) : of(this.currentUser));

      return getJwt$.pipe(
        filter((jwt: Jwt) => jwt != null),
        concatMap((jwt: Jwt) => getUser$(jwt)),
        defaultIfEmpty(null)
      );
    }
  }

  // TODO refresh token, error handling
  private getUserProfile(email: string): Observable<CompanyUser | AdminUser | PhysicianUser> {
    return this._http.get<any>(`api/private/profile/email/${email}`).pipe(
      map((result: any) => {
        this.currentUser = createUser(result);
        this.currentUserChanged$.next(this.currentUser);
        return this.currentUser;
      })
    );
  }

  private generatePassword(): string {
    let result = 'abcdefghijklmnopqrstuvwxyz'[Math.floor(Math.random() * 26)];
    result += 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'[Math.floor(Math.random() * 26)];
    result += Math.floor(Math.random() * 100);
    result += Math.random().toString(36).substring(7);
    return result;
  }

  private refreshToken() {
    const expires = new Date(this.jwt.exp.valueOf() * 1000);
    const timeout = expires.getTime() - Date.now() - 60 * 1000;
    this._refreshTokenTimeout = setTimeout(() => {
      this._auth0.checkSession(
        {
          responseType: 'token id_token',
          redirectUri: `${window.location.origin}`,
        },
        (err: any, authResult: any) => {
          if (authResult && authResult.accessToken) {
            this._cookieService.set(this._accessToken, authResult.accessToken, this.jwt.exp);
          }
        }
      );
    }, timeout);
  }
}
